import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { supabase } from "@/integrations/supabase/client";
import { scanStore } from "@/lib/scanStore";
import { toast } from "@/hooks/use-toast";
import { PaywallModal } from "@/components/PaywallModal";
import { useAuth } from "@/hooks/useAuth";
import { figVerifyToAIScan } from "@/lib/figVerifyMap";
import type { Json } from "@/integrations/supabase/types";

// Split a data URL like "data:image/jpeg;base64,XXXX" into { media_type, data }.
function splitDataUrl(dataUrl: string): { media_type: string; data: string } {
  const m = /^data:([^;]+);base64,(.*)$/.exec(dataUrl || "");
  if (m) return { media_type: m[1] || "image/jpeg", data: m[2] };
  return { media_type: "image/jpeg", data: dataUrl };
}



export default function Analyze() {
  const nav = useNavigate();
  const { user } = useAuth();
  // Analyze.tsx is now a transitional gating page. Its job is to: validate
  // photos, run the consume-scan paywall gate, encode photos, insert a
  // placeholder scans row, then *immediately* navigate to /result?scanId=...
  // where the new pending UI (ResultPending) takes over with the user's
  // photos + skeleton sections + cycling status text. fig-verify continues
  // to run in the background after navigation; Result.tsx subscribes via
  // Supabase Realtime and lights up when the result column populates.
  //
  // This replaces the old full-screen "Reading the threads…" loader. The
  // old progress bar, FIG_GUESSES cycling, and MIN_DWELL_MS dwell timer
  // are gone — Result.tsx is now responsible for waiting UX.
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const scanIdRef = useRef<string | null>(null);
  // Multi-photo set Fig will analyze. Falls back to the single legacy photo.
  const images = useRef<string[]>(
    scanStore.getAnalyzeImages().length > 0
      ? scanStore.getAnalyzeImages()
      : (scanStore.getPhoto() ? [scanStore.getPhoto() as string] : []),
  );
  const hints = useRef<string | null>(scanStore.getHints());
  const userBrand = useRef<string | null>(scanStore.getBrand());

  // Verify-existing-post mode: when the user hits "Verify now" on an
  // existing unverified post, Post.tsx stashes the post id + storage
  // URLs in sessionStorage. On mount we detect that, pull the photos
  // down as base64, and route the verification result back onto the
  // existing scan row instead of creating a duplicate placeholder.
  const verifyExistingId = useRef<string | null>(null);
  const verifyExistingPhotos = useRef<string[]>([]);
  const verifyExistingMeta = useRef<{ title?: string; brand?: string; category?: string }>({});
  useEffect(() => {
    try {
      const id = sessionStorage.getItem("fig_verify_existing_id");
      const photos = sessionStorage.getItem("fig_verify_existing_photos");
      const meta = sessionStorage.getItem("fig_verify_existing_meta");
      if (id) verifyExistingId.current = id;
      if (photos) verifyExistingPhotos.current = JSON.parse(photos) as string[];
      if (meta) verifyExistingMeta.current = JSON.parse(meta);
      // Clear immediately so a back-nav doesn't re-trigger.
      sessionStorage.removeItem("fig_verify_existing_id");
      sessionStorage.removeItem("fig_verify_existing_photos");
      sessionStorage.removeItem("fig_verify_existing_meta");
    } catch { /* sessionStorage may be blocked */ }
  }, []);

  // Kick off the AI call once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const isVerifyExisting = !!verifyExistingId.current && verifyExistingPhotos.current.length > 0;
      if (images.current.length === 0 && !isVerifyExisting) {
        // No photo captured — surface the error and let the user retry.
        scanStore.clearResult();
        scanStore.clearVerifyResult();
        setSetupError("No photo captured — please try scanning again.");
        return;
      }
      try {
        // Reset any previous run's verification payload before starting a
        // new scan so stale fig-verify data can never bleed into the report.
        scanStore.clearResult();
        scanStore.clearVerifyResult();
        scanStore.clearError();
        // FIG Verify gate: consume one scan from the user's budget. If they
        // have nothing left, bounce them to the plans tab paywall.
        const { data: gate } = await supabase.functions.invoke("consume-scan", { body: {} });
        if (gate && gate.ok === false && gate.paywall) {
          if (!cancelled) {
            scanStore.setError("paywall");
            setPaywallOpen(true);
          }
          return;
        }

        // Verify-existing branch: build the photos array from the storage
        // URLs stashed in sessionStorage instead of from scanStore data URLs.
        // Each URL is fetched + base64-encoded so fig-verify can send it to
        // Anthropic the same way a fresh scan does.
        let photos: Array<{ label: string; data: string; media_type: string }>;
        if (isVerifyExisting) {
          const urls = verifyExistingPhotos.current;
          const fetched = await Promise.all(
            urls.map(async (url, i) => {
              const resp = await fetch(url);
              if (!resp.ok) throw new Error(`Couldn't load photo ${i + 1}`);
              const blob = await resp.blob();
              const data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const result = String(reader.result || "");
                  const base64 = result.includes(",") ? result.split(",")[1] : result;
                  resolve(base64);
                };
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(blob);
              });
              return {
                label: i === 0 ? "Full item" : `Detail ${i}`,
                data,
                media_type: blob.type || "image/jpeg",
              };
            }),
          );
          photos = fetched;
        } else {
          const labels = scanStore.getPhotoLabels();
          photos = images.current.map((url, i) => {
            const { media_type, data } = splitDataUrl(url);
            return { label: labels[i] || `Photo ${i + 1}`, data, media_type };
          });
        }

        // Create a placeholder scan row so we have a stable ID to pass to
        // fig-verify. The edge function then writes the full result JSON back
        // to this row directly (service role), which is the authoritative write.
        // For the verify-existing flow we skip the insert and reuse the post's
        // existing id — fig-verify's update path will overwrite that row with
        // the verification result instead of creating a duplicate.
        let currentScanId: string | null = null;
        if (isVerifyExisting && user) {
          currentScanId = verifyExistingId.current!;
          scanIdRef.current = currentScanId;
          sessionStorage.setItem("fig_scan_saved_id", currentScanId);
          sessionStorage.setItem("fig_current_scan_id", currentScanId);
          // Reset processing_status so the row reads as "in flight" while
          // fig-verify runs. fig-verify will flip it to "complete" (or
          // "failed" via the rescue path) on its own write.
          await supabase
            .from("scans")
            .update({
              processing_status: "processing",
              error_message: null,
              completed_at: null,
            } as never)
            .eq("id", currentScanId);
        } else if (user) {
          // Snapshot the user's training-data consent at scan time. This is
          // the per-row gate used at training-extract time: a scan is only
          // eligible if both training_eligible=true here AND the user's
          // profiles.allow_training_use is still true at extract time.
          const { data: consentRow } = await supabase
            .from("profiles")
            .select("allow_training_use")
            .eq("id", user.id)
            .maybeSingle();
          const trainingEligible = !!(consentRow as { allow_training_use?: boolean } | null)?.allow_training_use;

          const { data: placeholder, error: placeholderErr } = await supabase
            .from("scans")
            .insert({
              user_id: user.id,
              title: "Vintage item",
              verdict: "FAIL",
              img: scanStore.getPhotos()[0] || "olive",
              photos: scanStore.getPhotos(),
              processing_status: "processing",
              review_status: "approved_closet",
              status: "kept",
              training_eligible: trainingEligible,
            } as never)
            .select("id")
            .single();
          if (placeholderErr) {
            console.error("placeholder insert failed", placeholderErr);
          } else if (placeholder?.id) {
            currentScanId = placeholder.id;
            scanIdRef.current = placeholder.id;
            sessionStorage.setItem("fig_scan_saved_id", placeholder.id);
            sessionStorage.setItem("fig_current_scan_id", placeholder.id);
          }
        }

        // ── Hand off to /result IMMEDIATELY ─────────────────────────────
        // Now that the scan row exists in DB with processing_status =
        // "processing", we navigate to /result?scanId=... so the user sees
        // the new pending UI (photos + cycling status + skeleton sections)
        // without waiting on fig-verify. The async function continues —
        // the fig-verify invocation below kicks off and writes the result
        // to the same scan row when done. Result.tsx subscribes via
        // Supabase Realtime and swaps pending → live the moment that
        // write happens.
        if (currentScanId && !cancelled) {
          nav(`/result?scanId=${currentScanId}`, { replace: true });
        }

        // Verify-existing pulls userBrand + itemCategory from the post's
        // saved meta (Post.tsx stashes them alongside the photos), since
        // scanStore never held that data for this flow.
        const fallbackBrand = isVerifyExisting
          ? (verifyExistingMeta.current?.brand ?? "")
          : (userBrand.current ?? "");
        const fallbackCategory = isVerifyExisting
          ? (verifyExistingMeta.current?.category ?? "")
          : (scanStore.getCategory() ?? "");
        const { data, error } = await supabase.functions.invoke("fig-verify", {
          body: {
            photos,
            userDetails: hints.current ?? "",
            userBrand: fallbackBrand,
            itemCategory: fallbackCategory,
            scanId: currentScanId,
          },
        });
        // A genuine invoke failure (network error, or a non-2xx that isn't the
        // 202 accept) still throws so the catch below marks the scan failed.
        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        // ── Background-execution contract (202) ─────────────────────────────
        // fig-verify now returns 202 { status: "processing" } for a real scan
        // (scanId present): the heavy pipeline runs as a Supabase background
        // task and writes the verdict to the scans row itself. Result.tsx picks
        // it up via Realtime + a 3s poll. So the "processing" ack carries NO
        // result in the body — we must NOT treat that as an error, and must NOT
        // run the response-body fallback DB write (it would clobber the row the
        // background task owns with placeholder data). We just stop here; the
        // navigate-to-/result above already handed the user off to the poller.
        const isProcessingAck =
          !!data && typeof data === "object" &&
          (data as { status?: string }).status === "processing";

        if (!isProcessingAck) {
          // scanId:null / legacy synchronous path — the verdict is returned
          // inline in the response body, so hydrate the store + persist as a
          // fallback in case the edge function couldn't write it.
          const verify = data as Record<string, any>;
          const ai = figVerifyToAIScan(verify, userBrand.current ?? "");
          scanStore.setVerifyResult(verify);
          scanStore.setResult(ai);

          if (currentScanId && user) {
            const { error: updateErr } = await supabase
              .from("scans")
              .update({
                result: verify as unknown as Json,
                title: ai.title || "Vintage item",
                brand: ai.brand || null,
                era: ai.era || null,
                origin: ai.origin || null,
                category: scanStore.getCategory() || ai.category || null,
                material: ai.material || null,
                size: ai.size || null,
                color: ai.color || null,
                condition: ai.condition || null,
                condition_notes: ai.condition_notes || null,
                verdict: ai.verdict,
                verdict_label: ai.verdict_label,
                confidence: ai.confidence,
                rarity: ai.rarity || null,
                tags: ai.tags ?? [],
                notes: ai.notes ?? [],
                price_low: ai.price_low ?? null,
                price_high: ai.price_high ?? null,
                price_suggested: ai.price_suggested ?? null,
                verification: (ai.verification ?? null) as unknown as Json,
                processing_status: "complete",
                completed_at: new Date().toISOString(),
              })
              .eq("id", currentScanId);
            if (updateErr) console.error("fallback result update failed", updateErr);
          }
        }

        // Hints and brand are single-use — drop them so the next scan starts clean.
        scanStore.clearHints();
        scanStore.clearBrand();
      } catch (e) {
        const msg = e instanceof Error ? e.message : "FIG assessment failed";
        console.error("fig-verify failed", e);
        scanStore.setError(msg);
        scanStore.clearResult();
        scanStore.clearVerifyResult();
        scanStore.clearHints();
        scanStore.clearBrand();

        // Mark the scan row as failed in DB so Result.tsx (already showing
        // the pending UI) can swap to an error state via its Realtime
        // subscription. Without this the row would sit in "processing"
        // until the 2-minute stuck-row safety net catches it.
        if (scanIdRef.current) {
          await supabase
            .from("scans")
            .update({
              processing_status: "failed",
              error_message: msg,
              completed_at: new Date().toISOString(),
            } as never)
            .eq("id", scanIdRef.current)
            .then(() => undefined, (err) => console.warn("mark-failed write failed:", err));
        }

        // If for some reason navigation didn't happen (no scan row was ever
        // created), surface a setup error on this page so the user has a
        // way out instead of seeing a blank "Preparing…" screen.
        if (!cancelled && !scanIdRef.current) {
          setSetupError(msg);
          toast({
            title: "Couldn't read this one",
            description: msg,
            variant: "destructive",
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Minimal "Preparing your scan…" view — visible only for the brief
  // window between mount and the nav("/result") call (consume-scan +
  // photo encode + placeholder insert, typically <2s). If something
  // fails during setup (no photo, paywall, scan-row insert error)
  // this is where the user lands and gets a recovery path.
  return (
    <>
      <MobileFrame>
        <div
          className="paper-bg"
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 40,
            gap: 16,
            textAlign: "center",
          }}
        >
          {setupError ? (
            <>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 20, fontWeight: 700, color: "hsl(var(--ink))",
                lineHeight: 1.25,
              }}>
                We couldn't start your scan.
              </div>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 14, color: "hsl(var(--ink-2))", maxWidth: 320,
              }}>
                {setupError}
              </div>
              <button
                onClick={() => nav("/scan", { replace: true })}
                className="btn btn-primary"
                style={{ padding: "12px 22px", marginTop: 8 }}
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <div
                aria-hidden
                style={{
                  width: 12, height: 12, borderRadius: "50%",
                  background: "hsl(var(--rust))",
                  animation: "fig-pulse 1s infinite",
                }}
              />
              <div className="caps" style={{ color: "hsl(var(--rust))", letterSpacing: "0.14em" }}>
                Preparing your scan…
              </div>
            </>
          )}
        </div>
      </MobileFrame>
      <PaywallModal open={paywallOpen} onClose={() => { setPaywallOpen(false); nav("/scan", { replace: true }); }} />
    </>
  );
}
